import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useAuth } from "@/lib/auth";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Wrench, LogIn, LogOut, Camera, AlertTriangle, ScanLine } from "lucide-react";
import { toast } from "sonner";
import { EquipmentScanner } from "@/components/EquipmentScanner";

export const Route = createFileRoute("/_authenticated/equipment")({
  component: EquipmentPage,
  head: () => ({ meta: [{ title: "Equipment — MissionReady" }] }),
});

type Equipment = {
  id: string; label: string; category: string | null; serial_number: string | null;
  status: "available" | "checked_out" | "damaged" | "retired";
  assigned_to_user_id: string | null; qr_token: string | null;
};
type Log = { id: string; equipment_id: string; action: string; condition_notes: string | null; photo_url: string | null; created_at: string; from_status: string | null; to_status: string | null };
type Action = "check_out" | "check_in" | "report_damaged";

const STATUS_COLOR: Record<Equipment["status"], string> = {
  available: "bg-emerald-600/20 text-emerald-400 border-emerald-500/40",
  checked_out: "bg-amber-600/20 text-amber-300 border-amber-500/40",
  damaged: "bg-[#CC0000]/20 text-[#CC0000] border-[#CC0000]/40",
  retired: "bg-zinc-600/20 text-zinc-300 border-zinc-500/40",
};

const ACTION_LABEL: Record<Action, string> = {
  check_out: "Check Out",
  check_in: "Check In",
  report_damaged: "Report Damaged",
};

function EquipmentPage() {
  const { currentOrg, user } = useAuth();
  const [items, setItems] = useState<Equipment[]>([]);
  const [logs, setLogs] = useState<Log[]>([]);
  const [open, setOpen] = useState(false);
  const [target, setTarget] = useState<Equipment | null>(null);
  const [action, setAction] = useState<Action>("check_out");
  const [notes, setNotes] = useState("");
  const [photoUrl, setPhotoUrl] = useState<string | null>(null);
  const [scannerOpen, setScannerOpen] = useState(false);

  const load = async () => {
    if (!currentOrg || !user) return;
    const [{ data: e }, { data: l }] = await Promise.all([
      supabase.from("equipment").select("*").eq("organization_id", currentOrg.id).eq("assigned_to_user_id", user.id),
      supabase.from("equipment_logs").select("*").eq("organization_id", currentOrg.id).eq("officer_id", user.id).order("created_at", { ascending: false }).limit(50),
    ]);
    setItems((e ?? []) as Equipment[]);
    setLogs((l ?? []) as Log[]);
  };
  useEffect(() => { load(); }, [currentOrg?.id, user?.id]);

  const startAction = (item: Equipment, act: Action) => {
    setTarget(item); setAction(act); setNotes(""); setPhotoUrl(null); setOpen(true);
  };

  const onScan = async (token: string) => {
    if (!currentOrg) return;
    const { data, error } = await supabase
      .from("equipment")
      .select("*")
      .eq("organization_id", currentOrg.id)
      .eq("qr_token", token.trim())
      .maybeSingle();
    if (error || !data) return toast.error("Equipment not found for this QR");
    const item = data as Equipment;
    // Choose default action based on status
    const defaultAction: Action =
      item.status === "checked_out" ? "check_in" :
      item.status === "available" ? "check_out" : "report_damaged";
    startAction(item, defaultAction);
  };

  const uploadPhoto = async (file: File) => {
    if (!currentOrg || !user) return;
    const safe = file.name.replace(/[^a-zA-Z0-9._-]/g, "_");
    const path = `${currentOrg.id}/${user.id}/${Date.now()}-${safe}`;
    const { error } = await supabase.storage.from("equipment-logs").upload(path, file);
    if (error) return toast.error(error.message);
    const { data } = await supabase.storage.from("equipment-logs").createSignedUrl(path, 60 * 60 * 24 * 365);
    setPhotoUrl(data?.signedUrl ?? null);
  };

  const submit = async () => {
    if (!currentOrg || !user || !target) return;
    const newStatus: Equipment["status"] =
      action === "check_in" ? "available" :
      action === "check_out" ? "checked_out" : "damaged";

    const { error: e1 } = await supabase.from("equipment_logs").insert({
      organization_id: currentOrg.id, equipment_id: target.id, officer_id: user.id,
      action, condition_notes: notes || null, photo_url: photoUrl,
      from_status: target.status, to_status: newStatus,
    });
    if (e1) return toast.error(e1.message);
    const { error: e2 } = await supabase.from("equipment").update({ status: newStatus }).eq("id", target.id);
    if (e2) return toast.error(e2.message);
    toast.success(ACTION_LABEL[action] + " logged");
    setOpen(false); load();
  };

  return (
    <div className="space-y-4 p-4">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Wrench className="h-6 w-6 text-[#CC0000]" />
          <div>
            <h1 className="text-2xl font-bold text-white">My Equipment</h1>
            <p className="text-sm text-white/60">Equipment assigned to you</p>
          </div>
        </div>
        <Button onClick={() => setScannerOpen(true)} className="bg-[#CC0000] hover:bg-[#a30000] text-white">
          <ScanLine className="h-4 w-4 mr-1" /> Scan QR
        </Button>
      </div>

      <div className="grid gap-2">
        {items.length === 0 && <p className="text-sm text-white/50">No equipment assigned. Use Scan QR to check out shared gear.</p>}
        {items.map((it) => (
          <div key={it.id} className="flex items-center justify-between rounded-lg border border-white/10 bg-white/5 p-3">
            <div className="min-w-0">
              <div className="font-medium text-white truncate">{it.label}</div>
              <div className="text-xs text-white/50">{it.category}{it.serial_number ? ` · SN ${it.serial_number}` : ""}</div>
            </div>
            <div className="flex items-center gap-2 flex-wrap justify-end">
              <Badge className={`border ${STATUS_COLOR[it.status]}`}>{it.status.replace("_", " ")}</Badge>
              {it.status === "available" && (
                <Button size="sm" onClick={() => startAction(it, "check_out")} className="bg-[#CC0000] hover:bg-[#a30000] text-white">
                  <LogOut className="h-3.5 w-3.5 mr-1" /> Check Out
                </Button>
              )}
              {it.status === "checked_out" && (
                <>
                  <Button size="sm" variant="outline" onClick={() => startAction(it, "check_in")}>
                    <LogIn className="h-3.5 w-3.5 mr-1" /> Check In
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => startAction(it, "report_damaged")} className="border-[#CC0000]/50 text-[#CC0000] hover:bg-[#CC0000]/10">
                    <AlertTriangle className="h-3.5 w-3.5 mr-1" /> Damaged
                  </Button>
                </>
              )}
            </div>
          </div>
        ))}
      </div>

      <div>
        <h2 className="mb-2 text-lg font-semibold text-white">Recent Activity</h2>
        <div className="space-y-2">
          {logs.length === 0 && <p className="text-sm text-white/50">No activity yet.</p>}
          {logs.map((l) => {
            const item = items.find((i) => i.id === l.equipment_id);
            return (
              <div key={l.id} className="rounded-lg border border-white/10 bg-white/5 p-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-white">
                    {item?.label ?? l.equipment_id} — <span className="text-[#CC0000]">{l.action.replace("_", " ")}</span>
                    {l.from_status && l.to_status && <span className="text-white/40 text-xs ml-2">{l.from_status} → {l.to_status}</span>}
                  </span>
                  <span className="text-white/50 text-xs">{new Date(l.created_at).toLocaleString()}</span>
                </div>
                {l.condition_notes && <div className="text-white/60 text-xs mt-1">{l.condition_notes}</div>}
                {l.photo_url && <a href={l.photo_url} target="_blank" rel="noreferrer"><img src={l.photo_url} alt="" className="h-16 mt-2 rounded border border-white/10" /></a>}
              </div>
            );
          })}
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{target ? `${ACTION_LABEL[action]}: ${target.label}` : ACTION_LABEL[action]}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Condition notes{action === "report_damaged" ? " *" : ""}</Label>
              <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder={
                action === "report_damaged" ? "Describe the damage…" :
                action === "check_in" ? "Any damage or issues?" : "Pre-use condition…"
              } />
            </div>
            <div>
              <Label className="mb-1 block">Photo {action === "report_damaged" ? "(strongly recommended)" : "(optional)"}</Label>
              {photoUrl ? <img src={photoUrl} alt="" className="h-24 rounded border border-white/10" /> : (
                <label className="inline-flex items-center gap-2 cursor-pointer rounded border border-dashed border-white/30 px-3 py-2 text-sm text-white/70 hover:bg-white/5">
                  <Camera className="h-4 w-4" /> Take photo
                  <input type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadPhoto(f); }} />
                </label>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button
              onClick={submit}
              disabled={action === "report_damaged" && !notes.trim()}
              className="bg-[#CC0000] hover:bg-[#a30000]"
            >
              Submit
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <EquipmentScanner open={scannerOpen} onOpenChange={setScannerOpen} onDetected={onScan} />
    </div>
  );
}
